import React, { useState, useRef, useEffect } from 'react';
import { Box, HStack, VStack, WarningIcon } from 'native-base';
import { CaptionError } from './Typography';
import { Input } from '~/shared/Theme';
import { useTheme } from '@springbig/shared';

const SegmentedInput = React.memo(({
    codeLength,
    onSubmit,
    error,
} : {
    codeLength: number,
    onSubmit: ({}) => void,
    error: string,
}) => {

    const {
        radius_sm,
    } = useTheme();

    const [code, setCode] =
        useState(Array(codeLength).fill(''));

    const inputRefs = useRef([]);

    useEffect(() => {

        inputRefs.current = inputRefs.current.slice(0, codeLength);
    }, [codeLength]);

    const handleCodeChange = (value, index) => {

        const updatedCode = [...code];
        updatedCode[index] = value;
        setCode(updatedCode);

        if (value && index < codeLength - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        if (value === '' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const onKeyDown = (event, index) => {

        if (event.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    useEffect(() => {

        const handlePaste = (event) => {

            event.preventDefault();
            const pastedData = (event.clipboardData || window.clipboardData).getData('text').trim();
            if (!pastedData) {
                return;
            }

            const updatedCode = pastedData.split('').slice(0, codeLength);
            setCode((prev) => [...updatedCode, ...prev.slice(updatedCode.length)]);

            const nextIndex = updatedCode.length - 1;
            if (inputRefs.current[nextIndex]) {
                inputRefs.current[nextIndex].focus();
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [codeLength]);

    useEffect(() => {

        if (code.join('').length === codeLength) {
            onSubmit({ pin: code.join('') });
        }
    }, [code]);

    useEffect(() => {

        setCode(Array(codeLength).fill(''));
    }, [codeLength]);

    return (
        <VStack justifyItems="center" alignItems="center">
            <HStack justifyItems="center" alignItems="center" space={codeLength === 4 ? 8 : 1}>
                { code.map((_, index) => (

                    <Input
                        key={index}
                        h={'75px'}
                        w={'60px'}
                        keyboardType={'numeric'}
                        maxLength={1}
                        textAlign={'center'}
                        value={code[index]}
                        onChangeText={(value) => handleCodeChange(value, index)}
                        ref={(el) => (inputRefs.current[index] = el)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                        fontSize={24}
                        fontFamily={'Helvetica'}
                        fontWeight={'bold'}
                        rounded={radius_sm * 2}
                    />
                ))}
            </HStack>
            { error &&
                <Box flexDirection="row" alignItems="center" mx={'auto'} pt={2}>
                    <WarningIcon size="4" mr="2" color={'red'} />
                    <CaptionError>{ error }</CaptionError>
                </Box>
            }
        </VStack>
    );
});

SegmentedInput.displayName = 'SegmentedInput';


export default SegmentedInput;
